'use client'

import React, { useContext, useEffect, useRef } from 'react'
import { useStore } from 'zustand'
import { Session } from 'next-auth'
import {
  AvailableScheduleResponse,
  PatientProfile,
  ProfileStatus,
} from '@/lib/apis/types'
import DocumentUploadFormStage from '@/shared/request/DocumentUploadFormStage'
import IHIVerifyFormStage from '@/shared/request/IHIVerifyFormStage'
import PersonalDetailsFormStage from '@/shared/request/PersonalDetailsFormStage'
import { ConsultationRequestFormContext } from '@/features/consultations/request/ConsultationRequestFormProvider'
import ConsultationQuestionnaireStageContainer from './ConsultationQuestionnaireStageContainer'
import { ConsultationRequestReviewStage } from '@/features/consultations/request/ConsultationRequestReviewStage'
import ConsultationCallArrangementStage from './ConsultationCallArrangementStage'
import {
  PrescriptionQuizHeader,
  PrescriptionQuizPage,
  prescriptionStepMotion,
} from '@/features/prescriptions/request/PrescriptionQuizShell'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'

type Props = {
  session: Session | null
  profile: PatientProfile | null
  profileStatus: ProfileStatus | null
  availableSlots: AvailableScheduleResponse
}

export default function ConsultationRequestForm({
  session,
  profile,
  profileStatus,
  availableSlots,
}: Props) {
  const store = useContext(ConsultationRequestFormContext)
  if (!store)
    throw new Error(
      'ConsultationRequestForm must be used within a ConsultationRequestFormContextProvider',
    )

  const currentStageIdx = useStore(store, (s) => s.currentStageIdx)
  const stages = useStore(store, (s) => s.stages)
  const currentStage = stages[currentStageIdx]
  const updateLoginState = useStore(store, (s) => s.updateLoginState)
  const reset = useStore(store, (s) => s.reset)
  const hasResetOnMount = useRef(false)
  const reduceMotion = useReducedMotion()
  const currentStep = currentStageIdx + 1
  const totalSteps = stages.length

  // Whenever this gets mounted, reset the form. Resetting in the submission page will have a chance to throw an error
  // since some components will get their invariants invalidated
  useEffect(() => {
    if (hasResetOnMount.current) return

    hasResetOnMount.current = true
    reset(availableSlots)
  }, [reset, availableSlots])

  // When user log-in successfully, there is no other place to trigger this than listening for `session` change
  useEffect(
    () => updateLoginState(session, profile, profileStatus),
    [session, profile, profileStatus, updateLoginState],
  )

  if (currentStage.type === 'IHI-VERIFICATION') {
    return (
      <IHIVerifyFormStage
        currentStep={currentStep}
        mobileServiceLabel="Request care · Consultation"
        serviceLabel="Request care · General consultation"
        totalSteps={totalSteps}
      />
    )
  }

  function resetScrollAfterStageExit() {
    const root = document.documentElement
    const previousScrollBehavior = root.style.scrollBehavior
    const previousPriority = root.style.getPropertyPriority('scroll-behavior')
    root.style.setProperty('scroll-behavior', 'auto', 'important')
    void root.offsetHeight
    window.scrollTo(0, 0)
    window.requestAnimationFrame(() => {
      if (previousScrollBehavior)
        root.style.setProperty(
          'scroll-behavior',
          previousScrollBehavior,
          previousPriority,
        )
      else root.style.removeProperty('scroll-behavior')
    })
  }

  return (
    <PrescriptionQuizPage>
      <AnimatePresence
        initial={!reduceMotion}
        mode="wait"
        onExitComplete={resetScrollAfterStageExit}
      >
        <motion.section
          animate="animate"
          exit={reduceMotion ? undefined : 'exit'}
          initial={reduceMotion ? false : 'initial'}
          key={`${currentStage.type}-${currentStage.type === 'QUESTIONNAIRE' ? currentStage.page : ''}`}
          variants={prescriptionStepMotion}
        >
          {currentStage.type !== 'CALL-ARRANGEMENT' && (
            <PrescriptionQuizHeader
              currentStep={currentStep}
              mobileServiceLabel="Request care · Consultation"
              serviceLabel="Request care · General consultation"
              title={stageTitle(currentStage)}
              totalSteps={totalSteps}
            />
          )}

          {(() => {
            switch (currentStage.type) {
              case 'QUESTIONNAIRE':
                return (
                  <ConsultationQuestionnaireStageContainer
                    stage={currentStage}
                  />
                )
              case 'UPLOAD-DOCUMENTS':
                return <DocumentUploadFormStage />
              case 'PERSONAL-INFORMATION':
                return <PersonalDetailsFormStage />
              case 'CALL-ARRANGEMENT':
                return <ConsultationCallArrangementStage />
              case 'REVIEW':
                return <ConsultationRequestReviewStage />
              default:
                return null
            }
          })()}
        </motion.section>
      </AnimatePresence>
    </PrescriptionQuizPage>
  )
}

function stageTitle(stage: { type: string; page?: number }) {
  switch (stage.type) {
    case 'QUESTIONNAIRE':
      return stage.page === 0
        ? "Tell us what's going on."
        : 'A few quick checks.'
    case 'UPLOAD-DOCUMENTS':
      return 'Add photos or documents.'
    case 'PERSONAL-INFORMATION':
      return 'Add your details.'
    case 'REVIEW':
      return 'Review your request.'
    default:
      return ''
  }
}
